"use client";

import { getCustomerDetails } from "@/dataLayer";
import { CustomerDetails } from "@/dataLayer/getCustomerDetails";
import { toastService } from "@/utils/toastService";
import { useEffect, useRef } from "react";

interface UseAuthLifecycleParams {
  isLoggedIn: boolean;
  isDineIn: boolean;
  isScanToOrderFlow: boolean;
  deleteSession: () => Promise<void>;
  refresh: () => void;
  merchantId: string;
  isMobileScreen: boolean;
  prevSession: { current: boolean };
  setCustomerDetails: React.Dispatch<
    React.SetStateAction<Partial<CustomerDetails> | null>
  >;
}

/**
 * Keeps customer authentication side effects outside the navigation render
 * function while preserving the existing login, scan-to-order, and toast flow.
 */
export const useAuthLifecycle = ({
  isLoggedIn,
  isDineIn,
  isScanToOrderFlow,
  deleteSession,
  refresh,
  merchantId,
  isMobileScreen,
  prevSession,
  setCustomerDetails,
}: UseAuthLifecycleParams): void => {
  const isMobileScreenRef = useRef(isMobileScreen);

  useEffect(() => {
    isMobileScreenRef.current = isMobileScreen;
  }, [isMobileScreen]);

  useEffect(() => {
    const handleCustomerInitialization = async () => {
      try {
        const details = await getCustomerDetails(merchantId);
        if (details) {
          setCustomerDetails(details);
          toastService.notifyUserLoggedIn(
            isMobileScreenRef.current,
            details?.firstName
          );
        }
      } catch (error) {
        console.error("User logged in, but profile fetch failed:", error);

        setCustomerDetails(null);
      }
    };

    const enforceAuthRules = async () => {
      if (isDineIn && isScanToOrderFlow && isLoggedIn) {
        await deleteSession();
        refresh();
        return;
      }

      const isNewLogin = isLoggedIn && !prevSession.current;
      if (isNewLogin && !isDineIn) {
        await handleCustomerInitialization();
      }

      prevSession.current = isLoggedIn;
    };

    enforceAuthRules();
  }, [
    deleteSession,
    isDineIn,
    isLoggedIn,
    isScanToOrderFlow,
    merchantId,
    prevSession,
    refresh,
    setCustomerDetails,
  ]);
};
